import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, X } from "lucide-react";

const PHOTOS = [
  {
    src: "/gallery/work-3.webp",
    caption: "Commercial rooftop exhaust — downtown Nashville",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    src: "/gallery/work-4.webp",
    caption: "Goodman package unit install",
    span: "",
  },
  {
    src: "/gallery/work-1.webp",
    caption: "Garage air handler & duct wrap",
    span: "",
  },
  {
    src: "/gallery/work-2.webp",
    caption: "Full system replacement, finished clean",
    span: "md:col-span-2",
  },
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-2xl"
        >
          <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em] text-ice">
            <Camera className="h-3.5 w-3.5" /> 05 — Photo Gallery
          </span>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Our work, up close.
          </h2>
          <p className="mt-4 text-base text-slate-400 sm:text-lg">
            Real installs, tune-ups, and upgrades across the state of Tennessee.
            Tap any photo for a closer look.
          </p>
        </motion.div>

        <div className="grid auto-rows-[170px] grid-cols-2 gap-3 sm:auto-rows-[200px] md:grid-cols-4">
          {PHOTOS.map((p, i) => (
            <motion.button
              key={p.src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`gallery-photo-${i + 1}`}
              onClick={() => setActive(i)}
              className={`group relative overflow-hidden rounded-xl border border-white/[0.08] transition-colors duration-300 hover:border-forge/50 ${p.span}`}
              aria-label={`View photo: ${p.caption}`}
            >
              <img
                src={p.src}
                alt={p.caption}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/85 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <span className="absolute bottom-3 left-4 translate-y-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-200 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                {p.caption}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-navy-950/95 p-4 backdrop-blur-xl sm:p-10"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setActive(null)}
              className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-white/5 text-slate-200 transition-colors hover:border-forge/60 hover:text-forge"
              aria-label="Close photo viewer"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={PHOTOS[active].src}
                alt={PHOTOS[active].caption}
                className="clip-frame max-h-[75vh] w-full border border-white/10 object-contain"
              />
              <figcaption className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
                {PHOTOS[active].caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
